/**
 * AddProductForm - Formulario para agregar productos al packing
 *
 * Permite al usuario agregar productos específicos al proceso de packing
 * desde una lista de productos disponibles. Incluye:
 * - Búsqueda y selección de productos con dropdown
 * - Control de cantidad con stepper y botón máximo
 * - Validación de cantidades disponibles por orden
 * - Integración con localStorage para persistir asignaciones
 * - Navegación automática al packing al seleccionar producto
 * - Soporte para productos dentro de packs
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { Product } from '@shared/components/ui/Product';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { usePackingContext } from '../../hooks';
import { type PackingProduct } from '../../models';

interface AddProductFormProps {
  onAddProduct: (productCode: string, quantity: number) => void;
  maxQuantity?: number;
  // Contexto de navegación: si estamos dentro de una orden y sus datos
  isInsideOrder?: boolean;
  selectedOrder?: any | null;
  assignedQuantities?: Record<string, number>;
  isSendingOrder?: boolean;
}

interface ProductOption {
  id: string;
  name: string;
  code: string;
  image: string;
  expectedQuantity: number;
  processedQuantity: number;
}

export const AddProductForm = ({
  onAddProduct,
  maxQuantity = 100,
  isInsideOrder = false,
  selectedOrder = null,
  assignedQuantities = {},
  isSendingOrder = false,
}: AddProductFormProps) => {
  // 🔍 LOG para verificar si el componente se está renderizando
  // Logs eliminados
  const { t } = useTranslation('packingModule');
  const { products, orders } = usePackingContext() as any;
  const [productCode, setProductCode] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductOption | null>(null);
  const [lastSearch, setLastSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Función para calcular cantidades completadas en pedidos terminados
  const getCompletedQuantityFromFinishedOrders = (productCode: string): number => {
    return (
      orders?.reduce((total: number, order: any) => {
        if (order.status_id !== 6) return total; // Solo pedidos terminados

        let orderCompletedQty = 0;
        (order.lines || []).forEach((line: any) => {
          if (line.pack && line.pack.lines) {
            // Es un pack - verificar cada producto del pack
            line.pack.lines.forEach((packLine: any) => {
              if (packLine.product?.code === productCode) {
                orderCompletedQty += packLine.qty || 0;
              }
            });
          } else if (line.product && line.product.code === productCode) {
            // Es un producto individual
            orderCompletedQty += line.qty || 0;
          }
        });

        return total + orderCompletedQty;
      }, 0) || 0
    );
  };

  // Calcular cantidad disponible del producto seleccionado
  // Si no hay producto seleccionado, usa maxQuantity como fallback
  // Si hay producto seleccionado, calcula: esperado - procesado = disponible
  // Faltante de la orden actual (si existe) usando SOLO memoria (assignedQuantities)
  const missingQtyFromFirstOrder = useMemo(() => {
    if (!selectedProduct) return 0;
    if (!isInsideOrder || !selectedOrder?.lines?.length) return 0;
    const line = (selectedOrder.lines as any[]).find((l: any) => l.product?.code === selectedProduct.code);
    if (!line) return 0;
    const key = `${selectedOrder.id}:${selectedProduct.code}`;
    const alreadyAssigned = Number(assignedQuantities[key] || 0);
    const expected = Number(line.qty || 0);
    // No restamos qty_done del backend; solo memoria
    return Math.max(0, expected - alreadyAssigned);
  }, [isInsideOrder, selectedOrder, selectedProduct, assignedQuantities]);

  const availableQuantity = useMemo(() => {
    if (!selectedProduct) return maxQuantity;

    // Obtener cantidad completada en pedidos terminados
    const completedFromFinishedOrders = getCompletedQuantityFromFinishedOrders(selectedProduct.code);

    // Priorizar faltante por orden detectada; si es 0, usar disponible global
    if (missingQtyFromFirstOrder > 0) return missingQtyFromFirstOrder;

    const assignedForCode = Object.entries(assignedQuantities)
      .filter(([key]) => key.endsWith(`:${selectedProduct.code}`))
      .reduce((acc, [, qty]) => acc + Number(qty), 0);

    // Calcular disponible: esperado - asignado - completado en pedidos terminados
    const available = Number(selectedProduct.expectedQuantity || 0) - assignedForCode - completedFromFinishedOrders;
    return Math.max(0, available);
  }, [
    selectedProduct,
    maxQuantity,
    missingQtyFromFirstOrder,
    assignedQuantities,
    orders, // Agregar orders como dependencia
  ]);

  // Handler para cerrar el dropdown al hacer click fuera del input o presionar ESC
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Verificar si el click fue fuera del contenedor del input y del dropdown
      const target = event.target as Node;
      const inputContainer = inputRef.current?.closest('.relative');

      if (inputContainer && !inputContainer.contains(target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    // Agregar event listeners cuando el dropdown está abierto
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    // Cleanup de los event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Opciones del desplegable
  // - Si estamos dentro de una orden: solo productos de la orden con faltante > 0
  // - Si no: todos los productos disponibles
  const productOptions: ProductOption[] = useMemo(() => {
    // Función auxiliar para construir URL completa de imagen
    const buildImageUrl = (imageHash: string) => {
      if (!imageHash) return '';
      if (imageHash.startsWith('http')) return imageHash;
      return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
    };

    if (isInsideOrder && selectedOrder?.lines?.length) {
      const lines: any[] = selectedOrder.lines;
      const orderProducts: ProductOption[] = [];

      lines.forEach((line: any) => {
        if (line.pack && line.pack.lines) {
          // Es un pack - agregar cada producto individual del pack
          line.pack.lines.forEach((packLine: any) => {
            const productOption: ProductOption = {
              id: (packLine.product?.id || '').toString(),
              name: packLine.product?.name || '',
              code: packLine.product?.code || '',
              image: buildImageUrl(packLine.product?.image_hash || ''),
              expectedQuantity: line.qty || 0, // La cantidad del pack se aplica a cada producto individual
              processedQuantity: line.qty_done || 0,
            };

            // Verificar si este producto individual tiene faltante
            const key = `${selectedOrder.id}:${productOption.code}`;
            const assigned = Number(assignedQuantities[key] || 0);
            const remaining = Number(productOption.expectedQuantity || 0) - assigned;

            if (remaining > 0) {
              orderProducts.push(productOption);
            }
          });
        } else if (line.product) {
          // Es un producto individual
          const productOption: ProductOption = {
            id: (line.product?.id || '').toString(),
            name: line.product?.name || '',
            code: line.product?.code || '',
            image: buildImageUrl(line.product?.image_hash || ''),
            expectedQuantity: line.qty || 0,
            processedQuantity: line.qty_done || 0,
          };

          // Verificar si este producto individual tiene faltante
          const key = `${selectedOrder.id}:${productOption.code}`;
          const assigned = Number(assignedQuantities[key] || 0);
          const remaining = Number(productOption.expectedQuantity || 0) - assigned;

          if (remaining > 0) {
            orderProducts.push(productOption);
          }
        }
      });

      return orderProducts;
    }

    const source = (products as PackingProduct[]) || [];

    const mapped = source
      .filter((product: PackingProduct) => {
        // Mostrar productos con faltante considerando pedidos terminados
        const expected = Number(product.expectedQuantity ?? 0);
        const assignedForCode = Object.entries(assignedQuantities)
          .filter(([key]) => key.endsWith(`:${product.code}`))
          .reduce((acc, [, qty]) => acc + Number(qty), 0);
        const completedFromFinishedOrders = getCompletedQuantityFromFinishedOrders(product.code);
        const available = expected - assignedForCode - completedFromFinishedOrders;
        return available > 0;
      })
      .map((product: PackingProduct) => ({
        id: product.id,
        name: product.name,
        code: product.code,
        image: product.image,
        expectedQuantity: product.expectedQuantity,
        processedQuantity: product.processedQuantity,
      }));
    // Si no hay opciones, el UI mostrará "Todos los productos están completados"
    return mapped;
  }, [products, isInsideOrder, selectedOrder, assignedQuantities, orders]);

  // Filtrar productos reales del contexto basado en lo que escribes en el input
  const filteredProducts = useMemo(() => {
    // Si no hay texto de búsqueda, mostrar todos los productos
    if (!productCode.trim()) return productOptions;

    // Si hay texto de búsqueda, filtrar por nombre o código
    return productOptions.filter(
      (product) =>
        product.name.toLowerCase().includes(productCode.toLowerCase()) ||
        product.code.toLowerCase().includes(productCode.toLowerCase()),
    );
  }, [productOptions, productCode]);

  const handleProductSelect = (code: string) => {
    const product = productOptions.find((p) => p.code === code);
    if (product) {
      setSelectedProduct(product);
      setProductCode(code);
      setLastSearch(productCode); // Guardar la última búsqueda
      setQuantity(0); // Resetear cantidad al seleccionar nuevo producto
    }
    setIsOpen(false);

    // Abrir pantalla de Packing inmediatamente al seleccionar un producto
    // Iniciamos el contador en 0 / qty según requerimiento
    if (product) {
      onAddProduct(product.code, 0);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProductCode(value);
    setSelectedProduct(null);
    setIsOpen(value.trim().length > 0);
  };

  const handleInputFocus = () => {
    // Mostrar todos los productos cuando se hace focus en el input
    setIsOpen(true);
  };

  const handleProductClick = () => {
    setSelectedProduct(null);
    setProductCode(lastSearch);
    setIsOpen(lastSearch.trim().length > 0);

    // Enfocar el input después de que se renderice
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleAddProduct = () => {
    if (productCode.trim() && quantity > 0) {
      onAddProduct(productCode.trim(), quantity);
      setProductCode('');
      setQuantity(0);
      setIsOpen(false);
      setSelectedProduct(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && productCode.trim() && quantity > 0) {
      handleAddProduct();
    }
  };

  return (
    <div className="pb-6">
      {/* Layout principal */}
      <div className="flex flex-col lg:flex-row lg:items-end gap-6 justify-between">
        {/* Lado izquierdo - Campos de entrada */}
        <div className="space-y-4">
          {/* Campos de producto y cantidad en fila */}
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            {/* Campo de producto */}
            <div className="space-y-2 flex-1 w-full sm:max-w-[270px] sm:w-[270px]">
              <Label htmlFor="product" className="text-sm font-medium text-title">
                {t('add_product.product_label')}
              </Label>
              <div className="relative w-full sm:max-w-[270px]">
                {selectedProduct ? (
                  // Mostrar producto seleccionado
                  <div
                    className={`w-full bg-card-bg border border-card-border rounded-md px-3 flex items-center gap-3 h-10 ${
                      !isSendingOrder ? 'cursor-pointer hover:bg-hover-press' : 'cursor-not-allowed opacity-50'
                    }`}
                    onClick={!isSendingOrder ? handleProductClick : undefined}
                    data-testid="selected-product-display"
                  >
                    <img
                      src={selectedProduct.image}
                      alt={selectedProduct.name}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span className="text-title font-medium text-xs line-clamp-1">{selectedProduct.name}</span>
                  </div>
                ) : (
                  // Input para buscar producto
                  <>
                    <Input
                      ref={inputRef}
                      id="product"
                      type="text"
                      placeholder={t('add_product.search_placeholder')}
                      value={productCode}
                      onChange={handleInputChange}
                      onFocus={handleInputFocus}
                      onKeyPress={handleKeyPress}
                      className="w-full"
                      data-testid="product-input"
                      autoComplete="off"
                      disabled={isSendingOrder}
                    />

                    {/* Dropdown que aparece al hacer focus o escribir */}
                    {isOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-card-bg border border-card-border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto w-full max-w-[270px]">
                        {filteredProducts.length > 0 ? (
                          // Lista de productos encontrados
                          filteredProducts.map((product) => (
                            <div
                              key={product.id}
                              onClick={(e) => {
                                e.stopPropagation(); // Prevenir que el evento se propague
                                handleProductSelect(product.code);
                              }}
                              className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-hover-press border-b border-card-border last:border-b-0"
                              data-testid={`product-option-${product.code}`}
                            >
                              <Product
                                id={parseInt(product.id)}
                                name={product.name}
                                src={product.image}
                                code={product.code}
                                showTooltip={false}
                                enableLineClamp={true}
                                className="flex-1"
                              />
                            </div>
                          ))
                        ) : (
                          // Mensaje cuando no se encuentran resultados o no hay productos disponibles
                          <div className="flex items-center justify-center py-4 px-3 text-center">
                            <div className="text-subtext text-xs">
                              {productOptions.length === 0
                                ? t('add_product.all_products_completed')
                                : t('add_product.no_results')}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Selector de cantidad */}
            <div className="space-y-2 flex-shrink-0 w-full sm:w-auto">
              <div className="flex items-center gap-2 justify-between">
                <Label htmlFor="quantity" className="text-sm font-medium text-title">
                  {t('add_product.quantity_label')}
                </Label>
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className="w-3 h-3 text-icons"
                  title={t('add_product.quantity_help')}
                />
              </div>
              {/* Stepper y botón Max deshabilitados hasta seleccionar producto */}

              <div className="flex items-end">
                <StepperInput
                  value={quantity}
                  onChange={setQuantity}
                  min={0}
                  max={availableQuantity}
                  step={1}
                  disabled={!selectedProduct || availableQuantity === 0 || isSendingOrder} // Deshabilitado si no hay producto, no hay cantidad disponible, o se está enviando
                  data-testid="quantity-stepper"
                />
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    setQuantity(availableQuantity);
                  }}
                  disabled={
                    !selectedProduct || quantity >= availableQuantity || availableQuantity === 0 || isSendingOrder
                  } // Deshabilitado si no hay producto, ya está en máximo, no hay cantidad disponible, o se está enviando
                  className="text-xs ml-[-2px] rounded-l-none"
                  data-testid="quantity-max-button"
                >
                  {t('add_product.max_button')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Lado derecho - Botones de acción */}
        <div className="flex gap-3 w-full sm:w-auto justify-end">
          <Button
            onClick={handleAddProduct}
            disabled={!productCode.trim() || quantity <= 0 || isSendingOrder}
            data-testid="add-to-packing-button"
            size="lg"
          >
            {t('add_product.add_to_packing')}
          </Button>
        </div>
      </div>
    </div>
  );
};
